import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  CurrencyDollar, Plus, Pencil, Trash, MapPin, Truck, Receipt, MagnifyingGlass
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCommissionsPage() {
  const [commissions, setCommissions] = useState([]);
  const [zones, setZones] = useState([]);
  const [rates, setRates] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commDialog, setCommDialog] = useState(false);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [rateDialog, setRateDialog] = useState(false);
  const [settlementDialog, setSettlementDialog] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [commForm, setCommForm] = useState({ name: '', type: 'percentage', rate: '', flat_fee: '', apply_to: 'all', apply_to_value: '', min_order_value: '', max_commission: '' });
  const [zoneForm, setZoneForm] = useState({ name: '', states: '' });
  const [rateForm, setRateForm] = useState({ zone_id: '', weight_from: '0', weight_to: '0.5', base_rate: '', additional_rate_per_kg: '', cod_charge: '30', fulfillment_mode: 'all' });
  const [stlForm, setStlForm] = useState({ seller_id: '', period_start: '', period_end: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [cRes, zRes, rRes, sRes, selRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/commissions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/shipping-zones`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/shipping-rates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/settlements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sellers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
      ]);
      setCommissions(cRes.data);
      setZones(zRes.data);
      setRates(rRes.data);
      setSettlements(sRes.data);
      setSellers(selRes.data.sellers || selRes.data || []);
      setCategories(catRes.data.filter(c => c.level === 1));
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // Commission CRUD
  const handleSaveComm = async () => {
    try {
      const payload = { ...commForm, rate: parseFloat(commForm.rate) || 0, flat_fee: parseFloat(commForm.flat_fee) || 0, min_order_value: parseFloat(commForm.min_order_value) || 0, max_commission: parseFloat(commForm.max_commission) || 0 };
      if (editingComm) {
        await axios.patch(`${API_URL}/api/admin/commissions/${editingComm.rule_id}`, payload, { withCredentials: true });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API_URL}/api/admin/commissions`, payload, { withCredentials: true });
        toast.success('Rule created');
      }
      setCommDialog(false); setEditingComm(null); fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteComm = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await axios.delete(`${API_URL}/api/admin/commissions/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  // Zone CRUD
  const handleSaveZone = async () => {
    try {
      const payload = { name: zoneForm.name, states: zoneForm.states.split(',').map(s => s.trim()).filter(Boolean) };
      if (editingZone) {
        await axios.patch(`${API_URL}/api/admin/shipping-zones/${editingZone.zone_id}`, payload, { withCredentials: true });
        toast.success('Zone updated');
      } else {
        await axios.post(`${API_URL}/api/admin/shipping-zones`, payload, { withCredentials: true });
        toast.success('Zone created');
      }
      setZoneDialog(false); setEditingZone(null); fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm('Delete this zone?')) return;
    try { await axios.delete(`${API_URL}/api/admin/shipping-zones/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  // Rate CRUD
  const handleSaveRate = async () => {
    try {
      const zone = zones.find(z => z.zone_id === rateForm.zone_id);
      const payload = { ...rateForm, zone_name: zone?.name || '', weight_from: parseFloat(rateForm.weight_from), weight_to: parseFloat(rateForm.weight_to), base_rate: parseFloat(rateForm.base_rate), additional_rate_per_kg: parseFloat(rateForm.additional_rate_per_kg), cod_charge: parseFloat(rateForm.cod_charge) };
      await axios.post(`${API_URL}/api/admin/shipping-rates`, payload, { withCredentials: true });
      toast.success('Rate created');
      setRateDialog(false); fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteRate = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await axios.delete(`${API_URL}/api/admin/shipping-rates/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); } catch { toast.error('Failed'); }
  };

  // Settlement generation
  const handleGenerateSettlement = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/settlements/generate`, stlForm, { withCredentials: true });
      toast.success(`Settlement generated: Net payable Rs.${res.data.net_payable}`);
      setSettlementDialog(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSettlementAction = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/settlements/${id}`, { status }, { withCredentials: true });
      toast.success(`Settlement ${status}`);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const commColumns = [
    { key: 'name', label: 'Rule Name' },
    { key: 'type', label: 'Type', render: v => <span className="px-2 py-0.5 text-xs font-semibold rounded bg-[#7d64ff]/10 text-[#7d64ff] capitalize">{v}</span> },
    { key: 'rate', label: 'Rate %', render: (v, row) => row.type !== 'flat' ? `${v}%` : '-' },
    { key: 'flat_fee', label: 'Flat Fee', render: (v, row) => row.type !== 'percentage' ? fmt(v) : '-' },
    { key: 'apply_to', label: 'Applies To', render: (v, row) => <span className="capitalize">{v}{row.apply_to_value ? `: ${row.apply_to_value}` : ''}</span> },
    { key: 'is_active', label: 'Status', render: v => <span className={`px-2 py-0.5 text-xs font-semibold rounded ${v ? 'sp-badge-success' : 'sp-badge-neutral'}`}>{v ? 'Active' : 'Off'}</span> },
    { key: 'rule_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" data-testid={`edit-comm-${v}`} onClick={() => { setEditingComm(row); setCommForm({ name: row.name, type: row.type, rate: row.rate, flat_fee: row.flat_fee, apply_to: row.apply_to, apply_to_value: row.apply_to_value || '', min_order_value: row.min_order_value || '', max_commission: row.max_commission || '' }); setCommDialog(true); }}><Pencil size={15} /></Button>
        <Button variant="ghost" size="sm" className="text-[#ea4b5a]" data-testid={`delete-comm-${v}`} onClick={() => handleDeleteComm(v)}><Trash size={15} /></Button>
      </div>
    )}
  ];

  const zoneColumns = [
    { key: 'name', label: 'Zone Name' },
    { key: 'states', label: 'States', render: v => <span className="text-xs text-[#5a5c87]">{(v || []).join(', ')}</span> },
    { key: 'zone_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" data-testid={`edit-zone-${v}`} onClick={() => { setEditingZone(row); setZoneForm({ name: row.name, states: (row.states || []).join(', ') }); setZoneDialog(true); }}><Pencil size={15} /></Button>
        <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleDeleteZone(v)}><Trash size={15} /></Button>
      </div>
    )}
  ];

  const rateColumns = [
    { key: 'zone_name', label: 'Zone' },
    { key: 'weight_from', label: 'Weight From (kg)' },
    { key: 'weight_to', label: 'Weight To (kg)' },
    { key: 'base_rate', label: 'Base Rate', render: v => fmt(v) },
    { key: 'additional_rate_per_kg', label: 'Add. Rate/kg', render: v => fmt(v) },
    { key: 'cod_charge', label: 'COD Charge', render: v => fmt(v) },
    { key: 'fulfillment_mode', label: 'Mode', render: v => <span className="capitalize">{v}</span> },
    { key: 'rate_id', label: 'Actions', sortable: false, render: v => (
      <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleDeleteRate(v)}><Trash size={15} /></Button>
    )}
  ];

  const stlColumns = [
    { key: 'seller_name', label: 'Seller' },
    { key: 'period_start', label: 'Period Start', render: v => v ? new Date(v).toLocaleDateString() : '-' },
    { key: 'period_end', label: 'Period End', render: v => v ? new Date(v).toLocaleDateString() : '-' },
    { key: 'total_orders', label: 'Orders' },
    { key: 'total_order_value', label: 'GMV', render: v => fmt(v) },
    { key: 'total_commission', label: 'Commission', render: v => fmt(v) },
    { key: 'shipping_charges', label: 'Shipping', render: v => fmt(v) },
    { key: 'net_payable', label: 'Net Payable', render: v => <span className="font-semibold text-[#28cd8c]">{fmt(v)}</span> },
    { key: 'status', label: 'Status', render: v => {
      const colors = { pending: 'warning', approved: 'primary', paid: 'success', rejected: 'danger' };
      return <span className={`px-2 py-0.5 text-xs font-semibold rounded sp-badge-${colors[v] || 'neutral'} capitalize`}>{v}</span>;
    }},
    { key: 'settlement_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        {row.status === 'pending' && (
          <>
            <Button size="sm" className="bg-[#28cd8c] hover:bg-[#1fb076] text-white text-xs" onClick={() => handleSettlementAction(v, 'approved')} data-testid={`approve-stl-${v}`}>Approve</Button>
            <Button size="sm" variant="outline" className="text-[#ea4b5a] border-[#ea4b5a] text-xs" onClick={() => handleSettlementAction(v, 'rejected')}>Reject</Button>
          </>
        )}
        {row.status === 'approved' && (
          <Button size="sm" className="bg-[#7d64ff] hover:bg-[#6153a7] text-white text-xs" onClick={() => handleSettlementAction(v, 'paid')} data-testid={`pay-stl-${v}`}>Mark Paid</Button>
        )}
      </div>
    )}
  ];

  return (
    <DashboardLayout title="Commissions & Shipping" subtitle="Configure fees, shipping zones, rates, and settlements">
      <Tabs defaultValue="commissions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="commissions" data-testid="tab-commissions"><CurrencyDollar size={16} className="mr-1" />Commissions</TabsTrigger>
          <TabsTrigger value="zones" data-testid="tab-zones"><MapPin size={16} className="mr-1" />Shipping Zones</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates"><Truck size={16} className="mr-1" />Shipping Rates</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements"><Receipt size={16} className="mr-1" />Settlements</TabsTrigger>
        </TabsList>

        {/* Commissions Tab */}
        <TabsContent value="commissions">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setEditingComm(null); setCommForm({ name: '', type: 'percentage', rate: '', flat_fee: '', apply_to: 'all', apply_to_value: '', min_order_value: '', max_commission: '' }); setCommDialog(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-commission-btn">
              <Plus size={16} className="mr-1" /> Add Rule
            </Button>
          </div>
          <SortableTable columns={commColumns} data={commissions} pageSize={10} emptyMessage="No commission rules" emptyIcon={CurrencyDollar} />
        </TabsContent>

        {/* Zones Tab */}
        <TabsContent value="zones">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setEditingZone(null); setZoneForm({ name: '', states: '' }); setZoneDialog(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-zone-btn">
              <Plus size={16} className="mr-1" /> Add Zone
            </Button>
          </div>
          <SortableTable columns={zoneColumns} data={zones} pageSize={10} emptyMessage="No shipping zones" emptyIcon={MapPin} />
        </TabsContent>

        {/* Rates Tab */}
        <TabsContent value="rates">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setRateForm({ zone_id: '', weight_from: '0', weight_to: '0.5', base_rate: '', additional_rate_per_kg: '', cod_charge: '30', fulfillment_mode: 'all' }); setRateDialog(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-rate-btn">
              <Plus size={16} className="mr-1" /> Add Rate
            </Button>
          </div>
          <SortableTable columns={rateColumns} data={rates} pageSize={10} emptyMessage="No shipping rates" emptyIcon={Truck} />
        </TabsContent>

        {/* Settlements Tab */}
        <TabsContent value="settlements">
          <div className="flex gap-2 mb-4">
            <Button onClick={() => { setStlForm({ seller_id: '', period_start: '', period_end: '' }); setSettlementDialog(true); }} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="generate-settlement-btn">
              <Plus size={16} className="mr-1" /> Generate Settlement
            </Button>
          </div>
          <SortableTable columns={stlColumns} data={settlements} pageSize={10} emptyMessage="No settlements" emptyIcon={Receipt} />
        </TabsContent>
      </Tabs>

      {/* Commission Dialog */}
      <Dialog open={commDialog} onOpenChange={setCommDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingComm ? 'Edit Commission Rule' : 'New Commission Rule'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Rule Name *</Label>
              <Input value={commForm.name} onChange={e => setCommForm({ ...commForm, name: e.target.value })} className="mt-1 rounded-sm" data-testid="comm-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Type</Label>
                <Select value={commForm.type} onValueChange={v => setCommForm({ ...commForm, type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="comm-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="flat">Flat Fee</SelectItem>
                    <SelectItem value="mixed">Mixed (% + Flat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Applies To</Label>
                <Select value={commForm.apply_to} onValueChange={v => setCommForm({ ...commForm, apply_to: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="comm-apply-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Orders</SelectItem>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="seller">Specific Seller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {commForm.apply_to === 'category' && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Category</Label>
                <Select value={commForm.apply_to_value || "none"} onValueChange={v => setCommForm({ ...commForm, apply_to_value: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select category</SelectItem>
                    {categories.map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {commForm.type !== 'flat' && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Rate %</Label>
                  <Input type="number" step="0.1" value={commForm.rate} onChange={e => setCommForm({ ...commForm, rate: e.target.value })} className="mt-1 rounded-sm" data-testid="comm-rate-input" />
                </div>
              )}
              {commForm.type !== 'percentage' && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Flat Fee (Rs.)</Label>
                  <Input type="number" value={commForm.flat_fee} onChange={e => setCommForm({ ...commForm, flat_fee: e.target.value })} className="mt-1 rounded-sm" data-testid="comm-flat-input" />
                </div>
              )}
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setCommDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveComm} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-comm-btn" disabled={!commForm.name.trim()}>
                {editingComm ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zone Dialog */}
      <Dialog open={zoneDialog} onOpenChange={setZoneDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingZone ? 'Edit Zone' : 'New Shipping Zone'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Zone Name *</Label>
              <Input value={zoneForm.name} onChange={e => setZoneForm({ ...zoneForm, name: e.target.value })} className="mt-1 rounded-sm" data-testid="zone-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">States (comma-separated)</Label>
              <Input value={zoneForm.states} onChange={e => setZoneForm({ ...zoneForm, states: e.target.value })} className="mt-1 rounded-sm" placeholder="Delhi, Haryana, UP" data-testid="zone-states-input" />
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setZoneDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveZone} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-zone-btn" disabled={!zoneForm.name.trim()}>
                {editingZone ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog open={rateDialog} onOpenChange={setRateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Shipping Rate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Zone *</Label>
              <Select value={rateForm.zone_id || "none"} onValueChange={v => setRateForm({ ...rateForm, zone_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="rate-zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select zone</SelectItem>
                  {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id}>{z.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Weight From (kg)</Label>
                <Input type="number" step="0.1" value={rateForm.weight_from} onChange={e => setRateForm({ ...rateForm, weight_from: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Weight To (kg)</Label>
                <Input type="number" step="0.1" value={rateForm.weight_to} onChange={e => setRateForm({ ...rateForm, weight_to: e.target.value })} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Base Rate (Rs.)</Label>
                <Input type="number" value={rateForm.base_rate} onChange={e => setRateForm({ ...rateForm, base_rate: e.target.value })} className="mt-1 rounded-sm" data-testid="rate-base-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Add. Rate/kg</Label>
                <Input type="number" value={rateForm.additional_rate_per_kg} onChange={e => setRateForm({ ...rateForm, additional_rate_per_kg: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">COD Charge</Label>
                <Input type="number" value={rateForm.cod_charge} onChange={e => setRateForm({ ...rateForm, cod_charge: e.target.value })} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setRateDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveRate} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-rate-btn" disabled={!rateForm.zone_id || !rateForm.base_rate}>Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settlement Dialog */}
      <Dialog open={settlementDialog} onOpenChange={setSettlementDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Settlement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Seller *</Label>
              <Select value={stlForm.seller_id || "none"} onValueChange={v => setStlForm({ ...stlForm, seller_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="stl-seller-select"><SelectValue placeholder="Select seller" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select seller</SelectItem>
                  {sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Period Start</Label>
                <Input type="date" value={stlForm.period_start} onChange={e => setStlForm({ ...stlForm, period_start: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Period End</Label>
                <Input type="date" value={stlForm.period_end} onChange={e => setStlForm({ ...stlForm, period_end: e.target.value })} className="mt-1 rounded-sm" />
              </div>
            </div>
            <div className="flex gap-4 pt-2">
              <Button variant="outline" onClick={() => setSettlementDialog(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleGenerateSettlement} className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="generate-stl-btn" disabled={!stlForm.seller_id}>Generate</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
